import { useCallback, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { STAGE_ROUTE } from '#/entities/shipment'
import type { ShipmentStage } from '#/entities/shipment'
import type { ShipmentStatus } from '#/shared/api/generated/shipment/models'
import { getGetShipmentApiV1ShipmentsShipmentIdGetQueryOptions } from '#/shared/api/generated/shipment/theCargoShipment'
import { useDetailSidebar } from '#/shared/ui/detail-sidebar'

import { ShipmentSidebarHeader } from '../ui/shipment-sidebar-header'
import { ShipmentSidebarPanel } from '../ui/shipment-sidebar-panel'

type SearchRecord = Record<string, unknown>

/**
 * Transient id→status hint set by the opener and read when the panel mounts, so
 * the toolbar can render optimistically. Kept out of the URL on purpose: the
 * `preview` param is the only deep-linkable bit, and a reload simply falls back
 * to the detail fetch (the map is empty until an opener runs in-session).
 */
const optimisticStatusById = new Map<string, ShipmentStatus>()

/** Drop the `preview` key from a search record, leaving the rest intact. */
function withoutPreview(prev: SearchRecord): SearchRecord {
  const next = { ...prev }
  delete next['preview']
  return next
}

/**
 * Open the shipment detail sidebar, kept in sync with the URL `preview` param
 * so the preview panel is deep-linkable and survives a reload — like the full
 * detail page. The returned opener writes the param; an effect mirrors it into
 * the global sidebar store, and closing/expanding strips it back out.
 */
export function useShipmentSidebar(stage: ShipmentStage) {
  const { open, dismiss, dismissEntry } = useDetailSidebar()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const raw: SearchRecord = useSearch({ strict: false })
  const previewRaw = raw['preview']
  const preview = typeof previewRaw === 'string' ? previewRaw : undefined
  const route = STAGE_ROUTE[stage]

  const clearPreview = useCallback(() => {
    void navigate({ search: withoutPreview as never, replace: true })
  }, [navigate])

  const expand = useCallback(
    (shipmentId: string) => {
      void queryClient.prefetchQuery(
        getGetShipmentApiV1ShipmentsShipmentIdGetQueryOptions(shipmentId),
      )
      void navigate({
        to: route.to as never,
        params: { [route.paramKey]: shipmentId } as never,
        search: withoutPreview as never,
      })
    },
    [navigate, queryClient, route.to, route.paramKey],
  )

  useEffect(() => {
    if (!preview) {
      dismiss()
      return
    }
    open({
      id: preview,
      header: <ShipmentSidebarHeader shipmentId={preview} />,
      content: (
        <ShipmentSidebarPanel
          stage={stage}
          shipmentId={preview}
          optimisticStatus={optimisticStatusById.get(preview)}
        />
      ),
      onExpand: () => expand(preview),
      onClose: clearPreview,
    })
    // The entry this page opened dies with the page. Without this, a
    // workspace-tab switch leaks it: the router's path-change dismiss fires,
    // but React then reconnects the outgoing page's effects mid-transition
    // (Activity), re-running this open() from the still-captured URL after the
    // dismiss — and the panel outlives the page over the next tab. Scoped by
    // id so it never closes an entry another surface opened afterwards.
    return () => dismissEntry(preview)
  }, [preview, stage, open, dismiss, dismissEntry, expand, clearPreview])

  return useCallback(
    (shipmentId: string, optimisticStatus?: ShipmentStatus) => {
      if (optimisticStatus)
        optimisticStatusById.set(shipmentId, optimisticStatus)
      void navigate({
        search: ((prev: SearchRecord) => ({
          ...prev,
          preview: shipmentId,
        })) as never,
      })
    },
    [navigate],
  )
}
